import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Printer } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { api, eur } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import Seo from "@/components/Seo";

const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clearCart, refreshArtworks } = useStore();
  const { t } = useLang();
  const [state, setState] = useState("checking");
  const [order, setOrder] = useState(null);
  const polls = useRef(0);

  useEffect(() => {
    if (!sessionId) return setState("error");
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setOrder(data.order);
          setState("paid");
          clearCart();
          refreshArtworks();
          return;
        }
        if (["expired", "failed", "cancelled"].includes(data.payment_status)) return setState("failed");
      } catch {
        return setState("error");
      }
      if (++polls.current >= MAX_POLLS) return setState("timeout");
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="payment-success-page" className="mx-auto max-w-2xl px-6 pb-28 pt-36 sm:px-10">
      <Seo title={t("payment.successTitle")} description={t("seo.home")} />
      {state === "checking" && (
        <div data-testid="payment-checking" className="border border-bone/10 bg-ink-2 p-10 text-center">
          <p className="font-mono text-[10px] tracking-[0.35em] text-brass">{t("payment.checking")}</p>
          <div className="mx-auto mt-6 h-px w-24 animate-pulse bg-brass/60" />
        </div>
      )}
      {state === "paid" && order && (
        <>
          <div data-testid="payment-certificate" className="certificate-print border border-brass/40 bg-ink-2 p-8 text-center sm:p-10">
            <p className="font-mono text-[9px] tracking-[0.4em] text-brass">{t("checkout.certificate")}</p>
            <p className="mt-4 font-cursive text-5xl text-bone">DeMoivre</p>
            <p className="mt-2 font-mono text-[9px] tracking-[0.35em] text-bone-muted">{t("brand.subtitleCaps")}</p>
            <div className="mx-auto my-6 h-px w-24 bg-brass/50" />
            <p className="font-serif text-lg italic text-bone-dim">{t("checkout.orderConfirmed").replace("{id}", order.order_id)}</p>
            <p data-testid="payment-certificate-no" className="mt-4 font-mono text-[10px] tracking-[0.25em] text-brass">{order.certificate_no}</p>
            <ul className="mt-6 space-y-1">
              {order.items.map((i) => (
                <li key={i.artwork_id} className="font-mono text-[10px] tracking-[0.2em] text-bone-dim">
                  {i.title}{i.dwg_no ? ` — ${i.dwg_no}` : ""}
                </li>
              ))}
            </ul>
            <p data-testid="payment-total" className="mt-6 font-serif text-xl text-bone">Total — {eur(order.total)}</p>
          </div>
          <p className="mt-6 text-center text-sm text-bone-dim">
            {t("checkout.sentTo")} <span className="text-bone">{order.email_hint}</span>. {t("checkout.preparesNote")}
          </p>
          <div className="mt-8 flex justify-center gap-4">
            <button data-testid="certificate-print-button" onClick={() => window.print()} className="flex items-center gap-2 border border-bone/20 px-6 py-3 font-mono text-[10px] tracking-[0.3em] text-bone transition-colors hover:border-brass/60 hover:text-brass">
              <Printer size={13} /> {t("checkout.print")}
            </button>
            <Link data-testid="payment-back-shop" to="/shop" className="border border-brass/60 bg-brass/10 px-6 py-3 font-mono text-[10px] tracking-[0.3em] text-brass transition-all hover:bg-brass hover:text-ink">
              {t("checkout.done")}
            </Link>
          </div>
        </>
      )}
      {(state === "failed" || state === "error" || state === "timeout") && (
        <div data-testid="payment-problem" className="border border-bone/10 bg-ink-2 p-10 text-center">
          <p className="font-mono text-[10px] tracking-[0.35em] text-brass">{t(`payment.${state}`)}</p>
          <p className="mt-4 text-sm text-bone-dim">{t("payment.contactNote")}</p>
          <Link to="/commissions" className="mt-8 inline-block border border-bone/20 px-6 py-3 font-mono text-[10px] tracking-[0.3em] text-bone transition-colors hover:border-brass/60 hover:text-brass">
            {t("payment.contactCta")}
          </Link>
        </div>
      )}
    </div>
  );
}
